import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { setUserData, setUserError } from '../components/InviteCode/userSlice';
import {
  allPatientsTabEndpoint,
  hypertensionOptions,
  medicalHistoryAndConditionsEndpoint,
  patientMetricsEndpoint,
  patientPathwayInstanceEndpoint,
  patientPathwayTemplatesEndpoint,
  personsEndpoint,
  searchDeviceByIdEndpoint,
  stepsTakenToRuleOutSecondaryHypertensionDisplayLabels,
  surveyResponsesSearchEndpoint,
  therapyDataBPEndpoint,
  averageOverTimeframeEndpoint,
  averageBPEndpoint,
} from '@pages/Patients/PatientProfile/PatientDetailsTab/constants';
import {
  setCreatedPatientId,
  setCreatedNameId,
  setCreatedTelecomId,
  setCreatedPhoneId,
  setPatientEngagementOverviewData,
  setPatientExecuteListsData,
  setPatientHTNData,
  setPatientHypertensionResponseData,
  setPatientInstructionsListData,
  setPatientKeyBPData,
  setPatientMedicalHistoryData,
  setPatientMedicationsData,
  setPatientObservationResponseData,
  setPatientPathwayTemplatesResponseData,
  setPatientSurveyData,
  setPersonsData,
  setTherapyChartData,
  setTherapyChartHeartRateData,
  setTherapyChartTargetBPData,
  setPatientDemographicResponseData,
} from '@pages/Patients/PatientProfile/PatientDetailsTab/patientSlice';
import { setUserConsentData } from './globalSlices';
import {
  deleteBulkSearchMHCData,
  setBulkSearchMHCData,
  setBulkSearchTherapyData,
} from './bulkSearchSlice';
import {
  MHC_CODE,
  SURVEY_CODE,
  THERAPY_DATA_CODE,
  patientMHCOptions,
} from '../utils/constants/medicalCodes';
import {
  transformAdministrators,
  transformBulkTherapyDataSearch,
  transformConsentSource,
  transformConsentSourceList,
  transformDeviceId,
  transformMedicalHistory,
  transformMhcData,
  transformNavigationList,
  transformOrganizationData,
  transformOrganizationWithCountData,
  transformPathwayInstances,
  transformPathways,
  transformPathwaysWithCount,
  transformPatients,
  transformPersons,
  transformRequireReviewTabPatients,
  transformSolutions,
  transformSolutionsWithCount,
  transformSurveyResponses,
  transformSurveys,
  transformUsersList,
  transformSurvey,
  transformConsent,
  transformSurveyResponsesSearch,
} from './transformers';
import { Url } from './endpoints';
import transformMedications from './transformers/transformMedications';
import transformBulkMHCDataSearch from './transformers/transformBulkMHCDataSearch';
import {
  createMHCDataPayload,
  createTherapyDataPayload,
  demographicSectionUpdatePayload,
} from '@pages/Patients/PatientProfile/PatientDetailsTab/payloads';
import transformTherapyDataSearch from './transformers/transformTherapyDataSearch';
import transformActionResponse from '@ReduxServices/transformers/transformActionResponse.js';

dayjs.extend(utc);

const BASE_URL = import.meta.env.VITE_BASE_URL;
const ENV_NAME = import.meta.env.VITE_ENV_NAME;

const targetSBPEndpoint = `${therapyDataBPEndpoint}/systolic_target/`;

export const transformGetResponse = (res, _, { endpoint = null }) => {
  const filteredMedicalHistoryResponse = transformMedicalHistory(res);
  const count = res?.count;
  const firstResult = res?.results?.[0];
  let outputObj = {};

  switch (endpoint) {
    // TODO: figure out how to wrap into its own transformer file.
    case medicalHistoryAndConditionsEndpoint:
      outputObj.hypertensionDiagnosis = filteredMedicalHistoryResponse.filter((data) =>
        [...hypertensionOptions, { code: MHC_CODE.HTN_NOT_DIAGNOSED }].some(
          (option) => option.code === data?.code,
        ),
      );
      Object.values(patientMHCOptions).forEach((property) => {
        const foundData = filteredMedicalHistoryResponse.find((data) => data?.code === property);
        const statusDisplayOptions = { active: 'Yes', inactive: 'No', unknown: 'Unknown' };
        outputObj[property] = statusDisplayOptions[foundData?.status] ?? 'Unknown';
      });
      break;
    // TODO all references to /persons should be deprecated and replaced when new custom
    // patient details API becomes active
    case personsEndpoint:
      outputObj.patientMrn = firstResult?.identifier?.[0]?.value;
      break;
    case patientPathwayInstanceEndpoint:
      outputObj.patientPathwayInstanceExists = firstResult?.status === 'active';
      outputObj.patientPathwayInstance = res?.results;
      break;
    case searchDeviceByIdEndpoint:
      outputObj.deviceIdExists = count;
      break;
    default:
      outputObj = res;
      break;
  }

  return outputObj;
};

export const transformCreateResponse = (
  res,
  _,
  { payload: { code = '', type = '' }, endpoint },
) => {
  const outputObj = {};
  const count = res?.count;
  const firstResult = res?.results?.[0];
  const effectiveDateTime = firstResult?.effectiveDateTime;
  const codingCode = code?.coding?.[0]?.code ?? '';

  if (type === 'patient') {
    outputObj.patient = transformPatients(res);
  }

  if (res?.link_id === SURVEY_CODE.LATEST_QOL_SURVEY_LINK_ID) {
    outputObj.latestQualityOfLifeScore = res?.answer?.[0]?.value_integer;
    outputObj.latestQualityOfLifeDate = dayjs(res?.updatedAt ?? res?.createdAt).format(
      'MM/DD/YYYY',
    );
  }

  if (endpoint === surveyResponsesSearchEndpoint) {
    switch (code) {
      case SURVEY_CODE.QOL_EQ5D5L:
        outputObj.qualityOfLifeSurvey = res?.results?.map((survey) => ({
          qualityOfLifeSurveyContent: survey?.surveyContent?.item,
        }));
        outputObj.qualityOfLifeSurveyTableData = res?.results?.reverse()?.map((survey) => ({
          qualityOfLifeSurveyId: survey?.surveyId,
          qualityOfLifeSurveyScore: survey?.score,
          qualityOfLifeSurveyDate: survey?.createdAt
            ? dayjs(survey?.createdAt).format('MM/DD/YYYY')
            : 'N/A',
        }));
        break;
      case SURVEY_CODE.MED_ADHERENCE:
        outputObj.medAdherenceSurvey = res?.results?.map((survey) => ({
          medAdherenceSurveyContent: survey?.surveyContent?.item,
        }));
        outputObj.medAdherenceSurveyTableData = res?.results?.reverse()?.map((survey) => ({
          medAdherenceSurveyId: survey?.surveyId,
          medAdherenceSurveyScore: survey?.score,
          medAdherenceSurveyDate: survey?.createdAt
            ? dayjs(survey?.createdAt).format('MM/DD/YYYY')
            : 'N/A',
        }));
        break;
      default:
        break;
    }
  }

  if (endpoint === patientMetricsEndpoint) {
    switch (code) {
      case SURVEY_CODE.QOL_EQ5D5L:
        outputObj.qualityOfLifeSurveyMetrics = res?.qualityOfLifeCode;
        break;
      case SURVEY_CODE.MED_ADHERENCE:
        outputObj.medAdherenceSurveyMetrics = res?.medAdherenceCode;
        break;
      default:
        break;
    }
  }

  switch (code) {
    case THERAPY_DATA_CODE.RDN_REFERRED_IC:
      outputObj.RDN_REFERRED_IC =
        firstResult?.status === 'final' ? firstResult?.valueBoolean : null;
      outputObj.patientRDNUpdatedOn = firstResult?.updatedOn;
      outputObj.patientDeviceId = firstResult?.device_id;
      break;
    case THERAPY_DATA_CODE.HTN_LEVEL_OF_CONCERN:
      outputObj.HTN_LEVEL_OF_CONCERN = count;
      outputObj.concernLevel = firstResult?.valueInteger;
      outputObj.concernLevelUpdatedOn = dayjs(
        firstResult?.updatedAt ?? firstResult?.createdAt,
      ).format('MM/DD/YYYY');
      break;
    case THERAPY_DATA_CODE.RDN_CONS:
      outputObj.RDNConsultScheduled = firstResult?.valueBoolean;
      break;
    case THERAPY_DATA_CODE.RDN_CONS_COMP:
      outputObj.RDNConsultCompleted = firstResult?.valueBoolean;
      break;
    case THERAPY_DATA_CODE.RDN_PA_CONF:
      outputObj.PriorAuthConfirmed = firstResult?.valueBoolean;
      break;
    case THERAPY_DATA_CODE.RDN_PROC_SCHED:
      outputObj.ProcedureScheduled = firstResult?.valueBoolean;
      break;
    case THERAPY_DATA_CODE.RDN_PROC_COMP:
      outputObj.ProcedureCompleted = firstResult?.valueBoolean;
      break;
    case THERAPY_DATA_CODE.BP_PANEL_AT_ENROLL:
    case THERAPY_DATA_CODE.SYSTOLIC_BP_AT_ENROLL:
      // FIXME the response data we need can come in any of these places in the data structure, somebody who's not us
      // need to fix this and make it consistent; or we wait until BE finishes custom API for Patient Details page
      outputObj.SystolicBP_at_enroll_object =
        firstResult?.component?.[0]?.value_quantity ??
        firstResult?.component?.[0]?.valueQuantity ??
        firstResult?.valueQuantity ??
        res?.component?.[1]?.value_quantity ??
        res?.value_quantity ??
        res?.valueQuantity;
      outputObj.effectiveDateTime = dayjs.utc(effectiveDateTime).format('MM/DD/YYYY');
      // NOTE: this fall through pattern is bad practice - this funciton should be refactored to if else
      if (code !== THERAPY_DATA_CODE.BP_PANEL_AT_ENROLL) {
        break;
      }
    // eslint-disable-next-line
    case THERAPY_DATA_CODE.DIASTOLIC_BP_AT_ENROLL:
      // FIXME the response data we need can come in any of these places in the data structure, somebody who's not us
      // need to fix this and make it consistent; or we wait until BE finishes custom API for Patient Details page
      outputObj.DiastolicBP_at_enroll_object =
        firstResult?.component?.[1]?.value_quantity ??
        firstResult?.component?.[1]?.valueQuantity ??
        firstResult?.valueQuantity ??
        res?.component?.[1]?.value_quantity ??
        res?.value_quantity ??
        res?.valueQuantity;
      outputObj.effectiveDateTime = dayjs.utc(effectiveDateTime).format('MM/DD/YYYY');
      break;
    case THERAPY_DATA_CODE.HEART_RATE_BP:
      outputObj.heartRateData = res?.results?.map((entry) => {
        if (entry?.code?.coding?.[0]?.system === 'http://loinc.org') {
          const filteredHRComponent = entry?.component?.find(
            (component) => component?.code?.coding?.[0]?.code === THERAPY_DATA_CODE.HEART_RATE_BP,
          );
          return {
            heartRateValue: filteredHRComponent?.value_quantity?.value,
            heartRateUnit: filteredHRComponent?.value_quantity?.unit,
            heartRateDate: dayjs(entry?.createdAt).format('MM/DD/YYYY'),
          };
        }
        return 'N/A';
      });
      break;
    case THERAPY_DATA_CODE.SYSTOLIC_BP_TARGET:
      outputObj.targetSystolicBPData = res?.valueQuantity?.value;
      break;
    case THERAPY_DATA_CODE.CHOLESTEROL_TOTAL:
      outputObj.Cholesterol_total = firstResult?.valueQuantity;
      outputObj.Cholesterol_total_updatedAt = firstResult?.updatedAt;
      break;
    case THERAPY_DATA_CODE.CHOLESTEROL_HDL:
      outputObj.Cholesterol_HDL = firstResult?.valueQuantity;
      outputObj.Cholesterol_HDL_updatedAt = firstResult?.updatedAt;
      break;
    case THERAPY_DATA_CODE.CHOLESTEROL_LDL:
      outputObj.Cholesterol_LDL = firstResult?.valueQuantity;
      outputObj.Cholesterol_LDL_updatedAt = firstResult?.updatedAt;
      break;
    case THERAPY_DATA_CODE.HTN_DX_DURATION_ENROLL:
      outputObj.HTN_Dx_Duration_Enroll = {
        valueDateTime: firstResult?.valueDateTime
          ? dayjs(firstResult?.valueDateTime).format('YYYY')
          : '--',
      };
      break;
    case THERAPY_DATA_CODE.HEIGHT_IN:
      outputObj.Height_in = firstResult?.valueQuantity;
      outputObj.Height_in_updatedAt = firstResult?.updatedAt;
      break;
    case THERAPY_DATA_CODE.WEIGHT_LB:
      outputObj.Weight_lb = firstResult?.valueQuantity;
      outputObj.Weight_lb_updatedAt = firstResult?.updatedAt;
      break;
    case THERAPY_DATA_CODE.HTN_MED_STATUS:
      outputObj.htn_med_status = {
        display: firstResult?.code?.text,
        code: THERAPY_DATA_CODE.HTN_MED_STATUS,
        valueString: firstResult?.valueString,
      };
      break;
    case THERAPY_DATA_CODE.HTN_NOT_DIAGNOSED:
      outputObj.HTN_Not_Diagnosed = {
        display: firstResult?.code?.text,
        code: THERAPY_DATA_CODE.HTN_NOT_DIAGNOSED,
        value: firstResult?.value || 'inactive',
      };
      break;
    case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_SLEEP:
      outputObj.Secondary_HTN_Differential_Sleep = {
        display:
          stepsTakenToRuleOutSecondaryHypertensionDisplayLabels[
            THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_SLEEP
          ],
        code,
        value: firstResult?.valueBoolean,
      };
      break;
    case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_BCP:
      outputObj.Secondary_HTN_Differential_BCP = {
        display:
          stepsTakenToRuleOutSecondaryHypertensionDisplayLabels[
            THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_BCP
          ],
        code,
        value: firstResult?.valueBoolean,
      };
      break;
    case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_UA:
      outputObj.Secondary_HTN_Differential_UA = {
        display:
          stepsTakenToRuleOutSecondaryHypertensionDisplayLabels[
            THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_UA
          ],
        code,
        value: firstResult?.valueBoolean,
      };
      break;
    case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_ECG12:
      outputObj.Secondary_HTN_Differential_ECG12 = {
        display:
          stepsTakenToRuleOutSecondaryHypertensionDisplayLabels[
            THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_ECG12
          ],
        code,
        value: firstResult?.valueBoolean,
      };
      break;
    case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_CBC:
      outputObj.Secondary_HTN_Differential_CBC = {
        display:
          stepsTakenToRuleOutSecondaryHypertensionDisplayLabels[
            THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_CBC
          ],
        code,
        value: firstResult?.valueBoolean,
      };
      break;
    default:
      break;
  }

  // NOTE below are currently all for modal updates
  const filteredMedicalHistoryResponse = transformMedicalHistory({ results: [res] });
  if (endpoint === medicalHistoryAndConditionsEndpoint) {
    outputObj.hypertensionDiagnosis = filteredMedicalHistoryResponse;
    outputObj.isHypertensionDiagnosisUpdate = true;
  }

  switch (codingCode) {
    case THERAPY_DATA_CODE.HTN_MED_STATUS:
      outputObj.htn_med_status = {
        display: res.code?.coding?.[0]?.display,
        code: THERAPY_DATA_CODE.HTN_MED_STATUS,
        valueString: res?.valueString,
      };
      break;
    case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_SLEEP:
    case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_BCP:
    case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_UA:
    case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_ECG12:
    case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_CBC:
      outputObj[codingCode] = {
        display: res.code?.coding?.[0]?.display,
        code: codingCode,
        value: !!res?.valueBoolean,
      };
      break;
    case THERAPY_DATA_CODE.DIASTOLIC_BP_AT_ENROLL:
      outputObj.DiastolicBP_at_enroll_object = res?.valueQuantity ?? res?.value_quantity;
      outputObj.effectiveDateTime = dayjs.utc(effectiveDateTime).format('MM/DD/YYYY');
      break;
    case THERAPY_DATA_CODE.SYSTOLIC_BP_AT_ENROLL:
      outputObj.SystolicBP_at_enroll_object = res?.valueQuantity ?? res?.value_quantity;
      outputObj.effectiveDateTime = dayjs.utc(effectiveDateTime).format('MM/DD/YYYY');
      break;
    case THERAPY_DATA_CODE.CHOLESTEROL_TOTAL:
      outputObj.Cholesterol_total = res?.valueQuantity;
      break;
    case THERAPY_DATA_CODE.CHOLESTEROL_HDL:
      outputObj.Cholesterol_HDL = res?.valueQuantity;
      break;
    case THERAPY_DATA_CODE.CHOLESTEROL_LDL:
      outputObj.Cholesterol_LDL = res?.valueQuantity;
      break;
    default:
      break;
  }

  return Object.keys(outputObj).length === 0 ? res : outputObj;
};

/**
 * a function to create Api that fetch's data using a given end point
 */
const standardQueryCb = (value) => `${ENV_NAME}${value}`;

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders(headers) {
    const idToken =
      window.localStorage[Object.keys(window.localStorage)?.find((k) => k.includes('.idToken'))];
    if (idToken) {
      headers.set('Authorization', `Bearer ${idToken}`);
    }
    return headers;
  },
  credentials: 'include',
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    window.location.pathname = '/logout';
  }
  return result;
};

export const Api = createApi({
  baseQuery: baseQueryWithReauth,
  reducerPath: 'digitalHealthApi',
  endpoints: (build) => ({
    getOrganizations: build.query({
      query: standardQueryCb,
      transformResponse: transformOrganizationData,
    }),
    getOrganizationsWithCount: build.query({
      query: standardQueryCb,
      transformResponse: transformOrganizationWithCountData,
    }),
    getActions: build.query({
      query: () => Url.bootstrap.pathwayTemplate(),
      transformResponse: transformSurveys,
    }),
    getPathways: build.query({
      query: (params) => Url.pathway.templates(params),
      transformResponse: transformPathways,
    }),
    getPathwaysWithCount: build.query({
      query: (params) => Url.pathway.templates(params),
      transformResponse: transformPathwaysWithCount,
    }),
    getPathwayModalDropdownOptions: build.query({
      query: standardQueryCb,
    }),
    getPatientsByOrganization: build.query({
      query: (organizationId) => ({
        url: Url.patients.byOrganization(organizationId),
      }),
    }),
    getCareTeamPatients: build.query({
      query: (params) => ({
        url: Url.careTeam.getPatients(params),
      }),
    }),
    getPractitionersList: build.query({
      query: (params) => ({
        url: Url.practitioners(params),
      }),
    }),
    getPractitionersReferralList: build.query({
      query: (params) => ({
        url: Url.practitionersReferral(params),
      }),
    }),
    searchExternalPractitioners: build.mutation({
      query: (npi) => ({
        url: Url.externalPractitioners(npi),
        method: 'GET',
      }),
    }),
    getDeviceById: build.query({
      query: standardQueryCb,
      transformResponse: transformDeviceId,
    }),
    getSolutions: build.query({
      query: standardQueryCb,
      transformResponse: transformSolutions,
    }),
    getSolutionsWithCount: build.query({
      query: standardQueryCb,
      transformResponse: transformSolutionsWithCount,
    }),
    getPatients: build.query({
      query: standardQueryCb,
      transformResponse: transformPatients,
    }),
    getPersons: build.query({
      query: standardQueryCb,
      transformResponse: transformPersons,
    }),
    getTargetedPatient: build.query({
      query: standardQueryCb,
      transformResponse: transformRequireReviewTabPatients,
    }),
    getUsersList: build.query({
      query: standardQueryCb,
      transformResponse: transformUsersList,
    }),
    getEducation: build.query({
      query: standardQueryCb,
      // NOTE: PLACEHOLDER; although, response transformation may not be needed
      transformResponse: transformUsersList,
    }),
    getPathwayTemplateById: build.query({
      query: (id) => ({
        url: Url.pathway.templateById(id),
        method: 'GET',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setPatientPathwayTemplatesResponseData(data));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    getPathwayTemplates: build.query({
      query: ({ status = 'active', organizationId = '', carePathwayTypes = [] }, ...params) =>
        Url.pathway.templates({ status, organizationId, carePathwayTypes }, params),
      transformResponse: (res) => res?.results,
    }),
    getPathwayInstances: build.query({
      query: (params) => Url.pathway.listInstances(params),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const mappedData = transformPathwayInstances(data);
          const patientEngagementModel = {
            patientPathwayInstanceExists: mappedData[0]?.status === 'active',
            patientPathwayInstance: mappedData,
          };
          dispatch(setPatientEngagementOverviewData(patientEngagementModel));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    getConsentSource: build.query({
      query: (params) => Url.consentSource(params),
      transformResponse: transformConsentSource,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUserConsentData({ consentSourceData: data }));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    getConsentSourceList: build.query({
      query: (endpoint) => `${ENV_NAME}${endpoint}`,
      transformResponse: transformConsentSourceList,
    }),
    getConsent: build.query({
      query: ({ url }) => Url.consent(url),
      transformResponse: transformConsent,
    }),
    getNavigationList: build.query({
      query: (endpoint) => `${ENV_NAME}${endpoint}`,
      transformResponse: transformNavigationList,
    }),
    getPractitioners: build.query({
      query: (endpoint) => `${ENV_NAME}${endpoint}`,
      transformResponse: transformAdministrators,
    }),

    getAdministrators: build.query({
      query: (endpoint) => `${ENV_NAME}${endpoint}`,
      transformResponse: transformAdministrators,
    }),
    getMedicalHistory: build.query({
      query: (endpoint) => `${ENV_NAME}${endpoint}`,
      transformResponse: transformMedicalHistory,
    }),
    getMedicalHistoryAndConditions: build.query({
      query: () => Url.mhc.all(),
      transformResponse: transformMedicalHistory,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setPatientMedicalHistoryData(data));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    getUsersMe: build.query({
      query: () => Url.users.me(),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUserData(data));
        } catch (err) {
          console.error(err);
          dispatch(setUserError(err?.error || { status: 500 }));
        }
      },
    }),
    // TODO: after Release 3.0, verify whether this can be removed
    getMedications: build.query({
      query: ({ patientId }) => Url.patients.medications(patientId),
      transformResponse: transformMedications,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setPatientMedicationsData(data));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    getPatientHypertensionMedications: build.query({
      query: ({ patientId, page = 1, limit = 20 }) => ({
        url: Url.patients.medications(patientId),
        method: 'GET',
        params: { page, limit },
      }),
    }),
    getAverageBP: build.query({
      query: (params) => Url.therapy.bloodPressure.average(params),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setPatientKeyBPData(data));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    getSurveyResponses: build.query({
      query: (params) => Url.surveyResponses.all(params),
      transformResponse: transformSurveyResponses,
    }),
    getSurveyResponsesSearch: build.query({
      query: (payload) => ({
        url: Url.surveyResponses.search.search(),
        method: 'POST',
        body: payload,
      }),
      transformResponse: transformSurveyResponsesSearch,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setPatientSurveyData(data));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    getSurvey: build.query({
      query: (id) => Url.survey(id),
      transformResponse: transformSurvey,
    }),
    getHealthkitQuantitiesStatistics: build.query({
      query: (params) => ({
        url: Url.therapy.healthkitQuantitiesStatistics(),
        method: 'POST',
        body: params,
      }),
    }),
    getAverageBPOverTimeframe: build.query({
      query: (params) => Url.therapy.bloodPressure.averageOverTimeframe(params),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setTherapyChartData(data));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    getDailyVariability: build.query({
      query: (params) => Url.therapy.dailyVariability(params),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setTherapyChartData(data));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    getInstructionsForPatientInstructionListData: build.query({
      query: () => Url.instructions(),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(setPatientInstructionsListData({ instructionsList: data?.results }));
      },
    }),
    getSurveys: build.query({
      query: (params) => Url.surveys(params),
      transformResponse: transformActionResponse,
    }),
    getEducationActions: build.query({
      query: (params) => Url.educationActions(params),
      transformResponse: transformActionResponse,
    }),
    getInstructions: build.query({
      query: (params) => Url.instructions(params),
      transformResponse: transformActionResponse,
    }),
    getNavigations: build.query({
      query: (params) => Url.navigations(params),
      transformResponse: transformActionResponse,
    }),
    getActionDetails: build.query({
      query: ({ actionType, id }) => Url.actionDetails[actionType]?.(id),
    }),
    getCheckInviteEligibility: build.query({
      query: ({ userTypePath, userId }) => Url.checkInviteUserEligibility({ userTypePath, userId }),
    }),
    getInviteUser: build.query({
      query: ({ userTypePath, userId }) => Url.inviteUser({ userTypePath, userId }),
    }),
    getPatientFiles: build.query({
      query: (params) => Url.patients.getFiles(params),
    }),
    getActivityLog: build.query({
      query: (params) => Url.patients.activityLog(params),
    }),
    getPatientEmrOrders: build.query({
      query: (params) => Url.patientEnrollmentEmr.getOrders(params),
      providesTags: ['emrOrders'],
    }),
    getPatientEmrOrder: build.query({
      query: (orderId) => Url.patientEnrollmentEmr.getOrder(orderId),
    }),
    getPodsList: build.query({
      query: (params) => Url.pods.getPods(params),
    }),
    updatePatientEmrOrder: build.mutation({
      query: ({ orderId, payload }) => ({
        url: Url.patientEnrollmentEmr.updateOrders(orderId),
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['emrOrders'],
    }),
    updatePatientFile: build.mutation({
      query: ({ patientId, fileId, payload }) => ({
        url: Url.patients.actionFile({ patientId, fileId }),
        method: 'PUT',
        body: payload,
      }),
    }),
    updateOrganization: build.mutation({
      query: ({ id, payload }) => ({
        url: Url.organizations.update(id),
        method: 'PUT',
        body: payload,
      }),
    }),
    deletePatientFile: build.mutation({
      query: ({ patientId, fileId }) => ({
        url: Url.patients.actionFile({ patientId, fileId }),
        method: 'DELETE',
      }),
    }),
    getPatientFile: build.mutation({
      query: ({ patientId, fileId }) => ({
        url: Url.patients.actionFile({ patientId, fileId }),
      }),
    }),
    createPatientFilePresignedUrlS3: build.mutation({
      query: (payload) => ({
        url: Url.patients.creatPresignedUrlS3({ patientId: payload.patientId }),
        method: 'POST',
        body: payload,
      }),
    }),
    get: build.mutation({
      query: ({ params = '', endpoint }) => ({
        url: `${ENV_NAME}${endpoint}${params}`,
        method: 'GET',
      }),
      transformResponse: transformGetResponse,
      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        const { endpoint } = payload || {};

        try {
          const { data } = await queryFulfilled;
          switch (endpoint) {
            case medicalHistoryAndConditionsEndpoint:
              dispatch(setPatientMedicalHistoryData(data));
              break;
            case personsEndpoint:
              dispatch(setPersonsData(data));
              break;
            case patientPathwayInstanceEndpoint:
            case searchDeviceByIdEndpoint:
              dispatch(setPatientEngagementOverviewData(data));
              break;
            case patientPathwayTemplatesEndpoint:
              // NOTE patientPathwayTemplatesEndpoint has no transform logic, since we just need
              // to store the entire plain response; the default switch case takes care of this
              dispatch(setPatientPathwayTemplatesResponseData(data));
              break;
            case targetSBPEndpoint:
              dispatch(
                setTherapyChartTargetBPData({
                  currentTargetSystolicBP: {
                    value: data?.[0]?.valueQuantity?.value,
                    unit: data?.[0]?.valueQuantity?.unit,
                  },
                }),
              );
              break;
            case averageOverTimeframeEndpoint:
              dispatch(setTherapyChartData(data));
              break;
            case averageBPEndpoint:
              dispatch(setPatientKeyBPData(data));
              break;
            default:
              break;
          }
        } catch (err) {
          console.error(err?.error);
        }
      },
    }),
    update: build.mutation({
      query: ({ payload, endpoint }) => ({
        url: `${ENV_NAME}${endpoint}/${payload?.id}`,
        method: 'PUT',
        body: payload,
      }),
    }),
    create: build.mutation({
      query: ({ payload, endpoint, params }) => ({
        url: `${ENV_NAME}${endpoint}`,
        method: 'POST',
        body: payload,
        params,
      }),
      transformResponse: transformCreateResponse,
      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        let data;

        try {
          const res = await queryFulfilled;
          data = res?.data;
          const {
            payload: { code = '', linkId = '' },
            endpoint,
          } = payload;
          switch (endpoint) {
            case surveyResponsesSearchEndpoint:
            case patientMetricsEndpoint:
              dispatch(setPatientSurveyData(data));
              break;
            default:
              break;
          }

          const codingCode = payload?.payload?.code?.coding?.[0]?.code;

          switch (linkId) {
            case THERAPY_DATA_CODE.LatestQOLSurveyLinkId:
              dispatch(setPatientSurveyData(data));
              break;
            default:
              break;
          }
          switch (code) {
            case THERAPY_DATA_CODE.HTN_LEVEL_OF_CONCERN:
              dispatch(setPatientHTNData(data));
              break;
            case THERAPY_DATA_CODE.RDN_REFERRED_IC:
            case THERAPY_DATA_CODE.RDN_CONS:
            case THERAPY_DATA_CODE.RDN_CONS_COMP:
            case THERAPY_DATA_CODE.RDN_PA_CONF:
            case THERAPY_DATA_CODE.RDN_PROC_SCHED:
            case THERAPY_DATA_CODE.RDN_PROC_COMP:
              dispatch(setPatientEngagementOverviewData(data));
              break;
            case THERAPY_DATA_CODE.DIASTOLIC_BP_AT_ENROLL:
            case THERAPY_DATA_CODE.SYSTOLIC_BP_AT_ENROLL:
            case THERAPY_DATA_CODE.DIASTOLIC_BP:
            case THERAPY_DATA_CODE.SYSTOLIC_BP:
              dispatch(setPatientKeyBPData(data));
              dispatch(setPatientObservationResponseData(data));
              break;
            case THERAPY_DATA_CODE.HEART_RATE_BP:
              dispatch(setTherapyChartHeartRateData(data));
              break;
            case THERAPY_DATA_CODE.SYSTOLIC_BP_TARGET:
              dispatch(setTherapyChartTargetBPData(data));
              break;
            case THERAPY_DATA_CODE.CHOLESTEROL_TOTAL:
            case THERAPY_DATA_CODE.CHOLESTEROL_HDL:
            case THERAPY_DATA_CODE.CHOLESTEROL_LDL:
            case THERAPY_DATA_CODE.HEIGHT_IN:
            case THERAPY_DATA_CODE.WEIGHT_LB:
            case THERAPY_DATA_CODE.HTN_DX_DURATION_ENROLL:
            case THERAPY_DATA_CODE.BP_PANEL_AT_ENROLL:
              dispatch(setPatientObservationResponseData(data));
              break;
            case THERAPY_DATA_CODE.HTN_MED_STATUS:
            case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_SLEEP:
            case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_BCP:
            case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_UA:
            case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_ECG12:
            case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_CBC:
              dispatch(setPatientHypertensionResponseData(data));
              break;
            default:
              break;
          }
          switch (codingCode) {
            // NOTE for hypertension update modal calls
            case THERAPY_DATA_CODE.HTN_MED_STATUS:
            case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_SLEEP:
            case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_BCP:
            case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_UA:
            case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_ECG12:
            case THERAPY_DATA_CODE.SECONDARY_HTN_DIFFERENTIAL_CBC:
              dispatch(setPatientHypertensionResponseData(data));
              break;
            case THERAPY_DATA_CODE.DIASTOLIC_BP_AT_ENROLL:
            case THERAPY_DATA_CODE.SYSTOLIC_BP_AT_ENROLL:
            case THERAPY_DATA_CODE.DIASTOLIC_BP:
            case THERAPY_DATA_CODE.SYSTOLIC_BP:
              dispatch(setPatientKeyBPData(data));
              dispatch(setPatientObservationResponseData(data));
              break;
            case THERAPY_DATA_CODE.CHOLESTEROL_TOTAL:
            case THERAPY_DATA_CODE.CHOLESTEROL_HDL:
            case THERAPY_DATA_CODE.CHOLESTEROL_LDL:
              dispatch(setPatientObservationResponseData(data));
              break;
            default:
              break;
          }
          switch (endpoint) {
            case medicalHistoryAndConditionsEndpoint:
              dispatch(setPatientMedicalHistoryData(data));
              break;
            default:
              break;
          }
        } catch (err) {
          console.error(err);
        }
        return data;
      },
    }),
    createPatient: build.mutation({
      query: ({ payload, params }) => ({
        url: `${ENV_NAME}${allPatientsTabEndpoint}`,
        method: 'POST',
        body: payload,
        params,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        let data;
        try {
          const res = await queryFulfilled;
          data = res?.data;
          dispatch(setCreatedPatientId(data?.id));
          dispatch(setCreatedNameId(data?.name?.[0]?.id));
          dispatch(setCreatedTelecomId(data?.telecom?.[0]?.id));
          const phoneId = data?.telecom?.find((t) => t.system === 'phone')?.id;
          dispatch(setCreatedPhoneId(phoneId));
        } catch (err) {
          console.error(err);
        }
        return data;
      },
    }),
    getPatientsByParams: build.mutation({
      query: ({ payload, params }) => ({
        url: Url.patientLists.execute(),
        method: 'POST',
        body: payload,
        params,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        let data;
        try {
          const res = await queryFulfilled;
          data = res?.data;
          dispatch(setPatientExecuteListsData(data));
        } catch (err) {
          console.error(err);
        }
        return data;
      },
    }),
    createMHCBulkSearch: build.mutation({
      query: ({ patientId = '', codes = [], status = null }) => ({
        url: Url.mhc.bulkSearch(),
        method: 'POST',
        body: {
          patientId,
          codes,
          status,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const mappedData = transformBulkMHCDataSearch(data);
          dispatch(setBulkSearchMHCData(mappedData));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    createMHCData: build.mutation({
      query: ({
        mhcId = '',
        patientId = '',
        status = 'active',
        code = '',
        display = '',
        optionLabel = '',
        usersMeData = {},
      }) => ({
        url: Url.mhc.all(),
        method: 'POST',
        body: createMHCDataPayload({
          patientId,
          status,
          code,
          display,
          optionLabel,
          mhcId,
          usersMeData,
        }),
      }),
    }),
    updateMHCData: build.mutation({
      query: ({
        mhcId = '',
        patientId = '',
        status = 'active',
        code = '',
        display = '',
        optionLabel = '',
        usersMeData = {},
      }) => ({
        url: Url.mhc.update(mhcId),
        method: 'PUT',
        body: createMHCDataPayload({
          mhcId,
          patientId,
          code,
          status,
          display,
          optionLabel,
          usersMeData,
        }),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data.status === 'inactive') {
          dispatch(deleteBulkSearchMHCData(data?.code?.coding?.code));
        }

        if (data.status !== 'inactive') {
          const mappedData = transformMhcData({ data });
          dispatch(setBulkSearchMHCData(mappedData));
        }
      },
    }),
    createTherapyBulkSearch: build.mutation({
      query: ({ patientId = '', codes = [], status = null }) => ({
        url: Url.therapy.bulkSearch(),
        method: 'POST',
        body: {
          patientId,
          codes,
          status,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const mappedData = transformBulkTherapyDataSearch(data);
          dispatch(setBulkSearchTherapyData(mappedData));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    createTherapyData: build.mutation({
      query: ({
        patientId = '',
        status = 'final',
        code = {},
        component = [],
        display,
        valueString = null,
        valueBoolean = null,
        valueQuantity = null,
        valueDateTime = null,
        valueInteger = null,
        category = null,
        schemaName = null,
      }) => ({
        url: Url.therapy.all(),
        method: 'POST',
        body: createTherapyDataPayload({
          patientId,
          status,
          code,
          component,
          display,
          valueString,
          valueBoolean,
          valueQuantity,
          valueDateTime,
          valueInteger,
          category,
          schemaName,
        }),
      }),
    }),
    updateTherapyData: build.mutation({
      query: ({
        therapyDataId,
        code = '',
        patientId = '',
        display = '',
        status = 'final',
        valueQuantity = null,
        valueString = null,
        valueBoolean = null,
        valueDateTime = null,
        valueInteger = null,
      }) => ({
        url: Url.therapy.updateOrDelete(therapyDataId),
        method: 'PUT',
        body: createTherapyDataPayload({
          id: therapyDataId,
          patientId,
          display,
          code,
          status,
          valueQuantity,
          valueString,
          valueBoolean,
          valueDateTime,
          valueInteger,
        }),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const mappedData = transformTherapyDataSearch(data);
          dispatch(setBulkSearchTherapyData(mappedData));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    deleteTherapyData: build.mutation({
      query: (therapyDataId) => ({
        url: Url.therapy.updateOrDelete(therapyDataId),
        method: 'DELETE',
      }),
    }),
    createTherapySearch: build.mutation({
      query: ({ patientId = '', patientIds = [], code = '', query = {} } = {}) => ({
        url: Url.therapy.search(query),
        method: 'POST',
        body: {
          code,
          patientId,
          patientIds,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const mappedData = transformTherapyDataSearch(data);
          // NOTE: This is a bit nonsensical, but instead of creating a whole new piece of state to manage while we wait for all the disparate
          // multiple pieces to be added and for us to refactor the view pieces, we can just push the individual
          // therapy data searches and push it into the bulk state until the bulk search endpoint catches up.
          dispatch(setBulkSearchTherapyData(mappedData));
        } catch (err) {
          console.error(err);
        }
      },
    }),
    createPathwayTemplates: build.mutation({
      query: ({
        patientId = '',
        status = 'active',
        carePathwayTypes = [],
        name = '',
        displayName = '',
        actions = [],
        organizationId = '',
      }) => ({
        url: Url.pathway.templates(),
        method: 'POST',
        body: {
          patientId,
          status,
          carePathwayTypes,
          name,
          displayName,
          actions,
          organizationId,
        },
      }),
    }),
    createSystolicTargetBP: build.mutation({
      query: (payload) => ({
        url: Url.therapy.bloodPressure.systolicTarget(),
        method: 'POST',
        body: payload,
      }),
    }),
    addMemberToCareTeam: build.mutation({
      query: (payload) => ({
        url: Url.careTeam.addMemberToCareTeam(),
        method: 'POST',
        body: payload,
      }),
    }),
    removeMemberFromCareTeam: build.mutation({
      query: (relationId) => ({
        url: Url.careTeam.removeMemberFromCareTeam(relationId),
        method: 'DELETE',
      }),
    }),
    createIcReferralForm: build.mutation({
      query: (payload) => ({
        url: Url.icReferral.createForm(),
        method: 'POST',
        body: payload,
      }),
    }),
    updateIcReferralForm: build.mutation({
      query: ({ payload, formId }) => ({
        url: Url.icReferral.updateForm(formId),
        method: 'PUT',
        body: payload,
      }),
    }),
    updatePatient: build.mutation({
      query: (payload) => ({
        url: Url.patients.one(payload.patientId),
        method: 'PUT',
        body: payload,
      }),
    }),
    partialUpdatePatient: build.mutation({
      query: ({ payload, patientId }) => {
        return {
          url: Url.patients.one(patientId),
          method: 'PATCH',
          body: payload,
        };
      },
    }),
    partialUpdateAdministrator: build.mutation({
      query: ({ payload, id }) => {
        return {
          url: Url.updateAdministrator(id),
          method: 'PATCH',
          body: payload,
        };
      },
    }),
    patchPatient: build.mutation({
      query: ({ payload, patientId }) => ({
        url: Url.patients.one(patientId),
        method: 'PATCH',
        body: payload,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const transformedResForSelectedPatient = transformPatients({ results: [data] })
          ?.results?.[0];
        dispatch(setPatientDemographicResponseData(transformedResForSelectedPatient));
      },
    }),
    createPathwayInstance: build.mutation({
      query: ({ pathwayTemplateId, status, patientId }) => ({
        url: Url.pathway.instances(),
        method: 'POST',
        body: {
          pathwayTemplateId,
          status,
          patientId,
        },
      }),
    }),
    updateSnoozePatient: build.mutation({
      query: ({ body }) => ({
        url: Url.therapy.snoozePatient(),
        method: 'POST',
        body,
      }),
    }),
    // NOTE: This would be a nice replacement for UpdateModal's handleSubmitHtnInfoPart1 but it throws an error that looks like a rejected action.error
    // but it isn't actionable for us. Investigation required for the async queryFn
    createPathwayInstanceByPatientUpdate: build.mutation({
      async queryFn(
        {
          pathway = '',
          patientId = '',
          organizationId = '',
          status = 'active',
          firstName,
          lastName,
          email,
          birthDate,
          sexForClinicalUse,
          nameId,
          emailId,
        },
        { dispatch },
        _extraOptions,
        fetchWithBQ,
      ) {
        const formattedPayload = demographicSectionUpdatePayload({
          firstName,
          lastName,
          carePathwayType: pathway,
          email,
          birthDate,
          sexForClinicalUse,
          patientId,
          nameId,
          emailId,
        });
        const updatedPatientResponse = await fetchWithBQ({
          url: Url.patients.one(patientId),
          method: 'PUT',
          body: formattedPayload,
        });

        const pathwayTemplateResponse = await fetchWithBQ({
          url: Url.pathway.templates({ status, organizationId, carePathwayTypes: [pathway] }),
        });

        if (updatedPatientResponse?.error || pathwayTemplateResponse?.error) {
          console.error(
            'Error updating patient or finding pathway template',
            updatedPatientResponse?.error,
            pathwayTemplateResponse?.error,
          );
          return;
        }

        if (updatedPatientResponse?.data && pathwayTemplateResponse?.data?.count) {
          const pathwayTemplateId =
            pathwayTemplateResponse.data.results?.find((res) =>
              res.carePathwayTypes.includes(pathway),
            )?.id ?? '';

          if (!pathwayTemplateId) {
            console.error(`There was an error getting the pathway template id`);
            return;
          }

          const res = await fetchWithBQ({
            url: Url.pathway.instances(),
            method: 'POST',
            body: {
              status,
              pathwayTemplateId,
              patientId,
            },
          });

          if (res?.data) {
            dispatch(setPatientDemographicResponseData({ carePathwayType: pathway }));
          } else {
            console.error(`Error adding pathway instance to pathway for ${patientId}`, {
              pathwayTemplateId,
              patientId,
              err: res?.error ?? '',
            });
          }
        }
      },
    }),
    updatePathwayInstance: build.mutation({
      query: (payload) => ({
        url: Url.pathway.instanceById(payload.id),
        method: 'PUT',
        body: payload,
      }),
    }),
    deletePathwayInstance: build.mutation({
      query: (id) => ({
        url: Url.pathway.instanceById(id),
        method: 'DELETE',
      }),
    }),
    createAppAcknowledgement: build.mutation({
      query: ({ payload }) => ({
        url: Url.consentBaseUrl(),
        method: 'POST',
        body: payload,
      }),
    }),
    triggerIosTask: build.mutation({
      query: ({ payload }) => ({
        url: Url.patientPathway.triggerIOSTask(payload),
        method: 'POST',
        body: payload,
      }),
    }),
    removeIOSRdnTasks: build.mutation({
      query: ({ payload }) => ({
        url: Url.patientPathway.removeIOSRdnTasks(payload),
        method: 'POST',
        body: payload,
      }),
    }),
  }),
});

export const {
  useCreateMutation,
  useCreatePatientMutation,
  useCreateMHCBulkSearchMutation,
  useCreateTherapyBulkSearchMutation,
  useGetPatientsByParamsMutation,
  useGetPatientsByOrganizationQuery,
  useGetCareTeamPatientsQuery,
  useGetPractitionersListQuery,
  useSearchExternalPractitionersMutation,
  useGetPractitionersReferralListQuery,
  useLazyGetPractitionersReferralListQuery,
  useGetAdministratorsQuery,
  useGetActionsQuery,
  useGetAverageBPQuery,
  useGetAverageBPOverTimeframeQuery,
  useGetConsentSourceListQuery,
  useGetConsentSourceQuery,
  useGetConsentQuery,
  useGetDailyVariabilityQuery,
  useGetDeviceByIdQuery,
  useGetMedicalHistoryAndConditionsQuery,
  useGetMedicalHistoryQuery,
  useGetMedicationsQuery,
  useGetPatientHypertensionMedicationsQuery,
  useGetMutation,
  useGetInstructionsForPatientInstructionListDataQuery,
  useGetInstructionsQuery,
  useGetNavigationListQuery,
  useGetOrganizationsQuery,
  useGetOrganizationsWithCountQuery,
  useGetPathwayInstancesQuery,
  useGetPathwaysQuery,
  useGetPathwaysWithCountQuery,
  useGetPathwayModalDropdownOptionsQuery,
  useGetPatientsQuery,
  useGetPersonsQuery,
  useGetPractitionersQuery,
  useGetSolutionsQuery,
  useGetSolutionsWithCountQuery,
  useGetSurveysQuery,
  useGetSurveyResponsesQuery,
  useGetSurveyQuery,
  useLazyGetHealthkitQuantitiesStatisticsQuery,
  useGetHealthkitQuantitiesStatisticsQuery,
  useGetEducationActionsQuery,
  useGetNavigationsQuery,
  useGetTargetedPatientQuery,
  useGetUsersListQuery,
  useGetUsersMeQuery,
  useUpdateMutation,
  useCreateTherapyDataMutation,
  useCreateTherapySearchMutation,
  useUpdateTherapyDataMutation,
  useDeleteTherapyDataMutation,
  useCreateMHCDataMutation,
  useUpdateMHCDataMutation,
  useGetPathwayTemplatesQuery,
  useCreatePathwayTemplatesMutation,
  useCreateIcReferralFormMutation,
  useUpdateIcReferralFormMutation,
  useUpdatePatientMutation,
  usePartialUpdatePatientMutation,
  usePartialUpdateAdministratorMutation,
  usePatchPatientMutation,
  useCreatePathwayInstanceMutation,
  useCreatePathwayInstanceByPatientUpdateMutation,
  useUpdateSnoozePatientMutation,
  useCreateAppAcknowledgementMutation,
  useUpdatePathwayInstanceMutation,
  useDeletePathwayInstanceMutation,
  useGetActionDetailsQuery,
  useGetPathwayTemplateByIdQuery,
  useGetSurveyResponsesSearchQuery,
  useCreateSystolicTargetBPMutation,
  useAddMemberToCareTeamMutation,
  useRemoveMemberFromCareTeamMutation,
  useGetPatientFilesQuery,
  useGetActivityLogQuery,
  useUpdatePatientFileMutation,
  useUpdateOrganizationMutation,
  useDeletePatientFileMutation,
  useCreatePatientFilePresignedUrlS3Mutation,
  useGetPatientFileMutation,
  useGetPatientEmrOrdersQuery,
  useGetPatientEmrOrderQuery,
  useGetPodsListQuery,
  useUpdatePatientEmrOrderMutation,
  useTriggerIosTaskMutation,
  useRemoveIOSRdnTasksMutation,
} = Api;
